import { getApiErrorStatus } from "../api/errorUtils";

/**
 * Turns an Orders API error into copy store staff can act on.
 *
 * Raw backend validation text ("property X should not exist", "must be a
 * positive number") is never shown in the UI. Use `logOrderApiError` at the point
 * the error happens so a developer still sees exactly what the server rejected.
 *
 * `describeOrderApiError` is pure (no logging), so it is safe to call while
 * rendering.
 */

// Specific, stable server messages that deserve their own explanation, checked
// before the generic per-status mapping.
const SPECIFIC_RULES = [
  // A2 shipping policy.
  { pattern: /delivery fee for this order has changed/i, key: "ordersErrors.shippingFeeChanged" },
  { pattern: /delivery is not available to this country/i, key: "ordersErrors.shippingCountry" },
  { pattern: /follows the shipping policy|shippingFeeOverride/i, key: "ordersErrors.shippingFeeRule" },
  // Returned-to-sender refused for a legacy order with no tracked reservations.
  { pattern: /cannot be restored automatically/i, key: "ordersErrors.untrackedReturn" },
  { pattern: /already cancelled/i, key: "ordersErrors.alreadyCancelled" },
  {
    pattern: /sent to the courier|created shipment|shipment request to finish|current shipment state/i,
    key: "ordersErrors.lockedAfterShipment",
  },
  { pattern: /reopen the order/i, key: "ordersErrors.reopenRequired" },
  { pattern: /cannot exceed the catalog price/i, key: "ordersErrors.priceAboveCatalog" },
];

const CONFLICT_RULES = [
  {
    pattern: /insufficient|not enough stock|out of stock|cannot be reserved|no longer available/i,
    key: "ordersErrors.stock",
  },
  { pattern: /no sale price|missing_price/i, key: "ordersErrors.price" },
  { pattern: /override|must not exceed|manager access/i, key: "ordersErrors.priceOverride" },
  { pattern: /shipment|courier|abex/i, key: "ordersErrors.shipment" },
  { pattern: /variant|product|not found/i, key: "ordersErrors.productMissing" },
];

export function logOrderApiError(error, context = "orders") {
  if (!error) return;
  // eslint-disable-next-line no-console
  console.warn(`[${context}] API error`, error?.status, error?.data || error);
}

export function describeOrderApiError(error, t, fallbackKey = "ordersErrors.generic") {
  if (!error) return t(fallbackKey);

  if (error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR") {
    return t("ordersErrors.network");
  }

  const status = getApiErrorStatus(error);
  const serverMessage = String(error?.data?.message || "");

  if (status === 401) return t("ordersErrors.session");
  if (status === 403) {
    if (/delivery fee/i.test(serverMessage)) return t("ordersErrors.shippingFeeOverride");
    return /override|manager/i.test(serverMessage)
      ? t("ordersErrors.priceOverride")
      : t("ordersErrors.permission");
  }

  if (status === 400 || status === 409) {
    const specific = SPECIFIC_RULES.find((entry) => entry.pattern.test(serverMessage));
    if (specific) return t(specific.key);
  }

  if (status === 409) {
    const rule = CONFLICT_RULES.find((entry) => entry.pattern.test(serverMessage));
    return t(rule ? rule.key : "ordersErrors.state");
  }

  if (status === 404) {
    return t("ordersErrors.productMissing");
  }

  // 400/422: request validation. The raw class-validator text is technical and
  // never meaningful to staff, so it maps to one clear instruction.
  if (status === 400 || status === 422 || Array.isArray(error?.data?.details)) {
    return t("ordersErrors.invalidData");
  }

  if (status && status >= 500) {
    return t("ordersErrors.server");
  }

  return t(fallbackKey);
}
